import React, { useEffect, useState } from 'react';
import { Plus, Trash2, Edit3, X, Save, ChevronDown, ChevronRight } from 'lucide-react';
import api from '../../lib/api';
import { useToast } from '../../hooks/use-toast';

const EMPTY = { name: '', popup: false, requiredQty: 0, maxQty: 0, options: [{ name: '', price: 0 }] };

export default function AdminModifiers() {
  const [groups, setGroups] = useState([]);
  const [editing, setEditing] = useState(null); // null | 'new' | id
  const [form, setForm] = useState(EMPTY);
  const [expanded, setExpanded] = useState({});
  const { toast } = useToast();

  const load = () => api.get('/admin/modifier-groups').then((r) => setGroups(r.data));
  useEffect(() => { load(); }, []);

  const startNew = () => { setEditing('new'); setForm({ ...EMPTY, options: [{ name: '', price: 0 }] }); };
  const startEdit = (g) => {
    setEditing(g.id);
    setForm({
      name: g.name,
      popup: !!g.popup,
      requiredQty: g.requiredQty || 0,
      maxQty: g.maxQty || 0,
      options: g.options?.length ? g.options.map((o) => ({ ...o })) : [{ name: '', price: 0 }],
    });
  };
  const cancel = () => { setEditing(null); setForm(EMPTY); };

  const on = (k) => (e) => setForm({ ...form, [k]: e.target.type === 'checkbox' ? e.target.checked : (e.target.type === 'number' ? Number(e.target.value) : e.target.value) });
  const setOpt = (i, k, v) => setForm({ ...form, options: form.options.map((o, idx) => idx === i ? { ...o, [k]: k === 'price' ? Number(v) : v } : o) });
  const addOpt = () => setForm({ ...form, options: [...form.options, { name: '', price: 0 }] });
  const rmOpt = (i) => setForm({ ...form, options: form.options.filter((_, idx) => idx !== i) });

  const save = async (e) => {
    e.preventDefault();
    const cleanOptions = form.options.filter((o) => o.name?.trim());
    if (!form.name.trim() || cleanOptions.length === 0) {
      toast({ title: 'Missing info', description: 'Group name and at least one option are required.' });
      return;
    }
    const payload = { ...form, options: cleanOptions };
    try {
      if (editing === 'new') await api.post('/admin/modifier-groups', payload);
      else await api.put(`/admin/modifier-groups/${editing}`, payload);
      toast({ title: 'Saved', description: form.name });
      cancel(); load();
    } catch (err) {
      toast({ title: 'Error', description: err?.response?.data?.detail || 'Save failed' });
    }
  };

  const del = async (g) => {
    if (!window.confirm(`Delete "${g.name}"? This will also unlink it from all menu items.`)) return;
    try {
      await api.delete(`/admin/modifier-groups/${g.id}`);
      load();
    } catch (err) {
      toast({ title: 'Error', description: err?.response?.data?.detail || 'Delete failed' });
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-heading font-bold text-3xl text-[#0F1C2E]">Modifier Groups</h1>
          <p className="text-gray-500 text-sm mt-1">These control the customize options customers pick when adding items to cart (e.g. Choice of Meat, Dipping Sauce, Extras).</p>
        </div>
        <button onClick={startNew} className="btn-wrappus text-sm shrink-0"><Plus size={16} /> Add Group</button>
      </div>

      {editing && (
        <form onSubmit={save} className="bg-white rounded-2xl p-6 shadow-sm mb-6 space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="font-heading font-bold text-lg">{editing === 'new' ? 'New Modifier Group' : 'Edit Modifier Group'}</h2>
            <button type="button" onClick={cancel} className="p-2 hover:bg-gray-100 rounded"><X size={18} /></button>
          </div>
          <div className="grid md:grid-cols-2 gap-4">
            <F label="Group Name *"><input required value={form.name} onChange={on('name')} className="inp" placeholder="e.g. Choice of Meat" /></F>
            <F label="Pop-up Automatically">
              <label className="flex items-center gap-2 mt-2"><input type="checkbox" checked={form.popup} onChange={on('popup')} className="accent-[#ED2E2E]" /> Open this group by default when customizing</label>
            </F>
            <F label="Required Quantity (0 = optional)"><input type="number" min="0" value={form.requiredQty} onChange={on('requiredQty')} className="inp" /></F>
            <F label="Max Quantity (0 = unlimited, 1 = single-choice)"><input type="number" min="0" value={form.maxQty} onChange={on('maxQty')} className="inp" /></F>
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-xs font-semibold text-gray-600 uppercase">Options</label>
              <button type="button" onClick={addOpt} className="text-sm text-[#ED2E2E] font-semibold flex items-center gap-1"><Plus size={14} /> Add Option</button>
            </div>
            <div className="space-y-2">
              {form.options.map((o, i) => (
                <div key={i} className="flex gap-2 items-center">
                  <input value={o.name} onChange={(e) => setOpt(i, 'name', e.target.value)} placeholder="Option name (e.g. Chicken)" className="inp flex-1" />
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">+$</span>
                    <input type="number" step="0.01" min="0" value={o.price} onChange={(e) => setOpt(i, 'price', e.target.value)} className="inp w-28 pl-8" />
                  </div>
                  <button type="button" onClick={() => rmOpt(i)} className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded"><Trash2 size={16} /></button>
                </div>
              ))}
            </div>
          </div>

          <button type="submit" className="btn-wrappus text-sm"><Save size={16} /> Save</button>
          <style>{`.inp{width:100%;border-radius:0.75rem;border:1px solid #E5E7EB;background:white;padding:0.65rem 0.9rem;font-size:0.9rem;} .inp:focus{outline:none;border-color:#ED2E2E;box-shadow:0 0 0 3px rgba(237,46,46,0.15);} `}</style>
        </form>
      )}

      <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
        {groups.length === 0 ? <div className="p-8 text-center text-gray-400 text-sm">No modifier groups yet</div> : groups.map((g) => (
          <div key={g.id} className="border-b border-gray-50 last:border-b-0">
            <div className="flex items-center gap-3 px-5 py-4">
              <button onClick={() => setExpanded((e) => ({ ...e, [g.id]: !e[g.id] }))} className="p-1 hover:bg-gray-100 rounded">
                {expanded[g.id] ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
              </button>
              <div className="flex-1">
                <div className="font-heading font-semibold text-[#0F1C2E]">{g.name}</div>
                <div className="text-xs text-gray-500 mt-0.5">
                  {g.options?.length || 0} option{(g.options?.length || 0) !== 1 ? 's' : ''}
                  {(g.requiredQty || 0) > 0 && <span className="text-[#ED2E2E] font-semibold"> · Required (min {g.requiredQty})</span>}
                  {(g.maxQty || 0) > 0 && <span> · Max {g.maxQty}</span>}
                  {g.popup && <span> · Pop-up</span>}
                </div>
              </div>
              <button onClick={() => startEdit(g)} className="p-2 hover:bg-gray-100 rounded text-gray-500 hover:text-[#ED2E2E]"><Edit3 size={16} /></button>
              <button onClick={() => del(g)} className="p-2 hover:bg-red-50 rounded text-gray-500 hover:text-red-600"><Trash2 size={16} /></button>
            </div>
            {expanded[g.id] && (
              <div className="px-14 pb-4 space-y-1">
                {g.options?.map((o, i) => (
                  <div key={i} className="flex justify-between text-sm py-1.5 border-b border-gray-50 last:border-b-0">
                    <span className="text-gray-700">{o.name}</span>
                    <span className="font-semibold text-[#0F1C2E]">{o.price > 0 ? `+$${o.price.toFixed(2)}` : 'Free'}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function F({ label, children }) {
  return <div><label className="text-xs font-semibold text-gray-600 uppercase block mb-1">{label}</label>{children}</div>;
}
